import { useEffect } from 'react';

import { WHEEL_GESTURE_IDLE_MS } from '../config/slownessConfig';
import type { DiagramSlowness } from '../types/DiagramSlowness.types';
import { getGestureStutters, isUnusableGesture } from '../utils/diagramSlowness';

type GestureKind = 'zoom' | 'pan';

type UseSlowGestureDetectionParams = {
  /** The area of the chart; only wheel and drag gestures on its canvas count. */
  chartElement: HTMLElement | null;
  onSlowGesture: (slowness: Extract<DiagramSlowness, { kind: GestureKind }>) => void;
};

type Gesture = {
  kind: GestureKind;
  startTime: number;
  /** The last wheel or drag movement the page has handled. */
  lastInputTime: number;
  /** Undefined until the first frame, which is the baseline of the gaps. */
  lastFrameTime?: number;
  frameGaps: number[];
  /** A hidden tab gets no frames, so its gaps say nothing about the chart. */
  wasHidden: boolean;
};

function isOnChartCanvas(event: Event): boolean {
  return event.target instanceof HTMLCanvasElement;
}

/**
 * Watches wheel zooms and drag pans on the chart. While one lasts, it times the gaps between the
 * frames the browser paints; a gesture with repeated long gaps (see isUnusableGesture) is reported.
 *
 * A zoom ends in a frame, not on a timer: the browser hands over pending input before each frame,
 * so a frame long after the last wheel event means the wheel really stopped. A timer would fire
 * while the chart is still busy and cut a slow zoom into pieces, missing exactly the slow case.
 */
export function useSlowGestureDetection({ chartElement, onSlowGesture }: UseSlowGestureDetectionParams) {
  useEffect(() => {
    if (!chartElement) {
      return undefined;
    }

    let gesture: Gesture | undefined;
    let frameId: number | undefined;
    let isPointerPressedOnCanvas = false;

    function recordFrame(frameTime: number) {
      if (!gesture) {
        return;
      }

      if (gesture.lastFrameTime !== undefined) {
        gesture.frameGaps.push(frameTime - gesture.lastFrameTime);
      }

      gesture.lastFrameTime = frameTime;

      if (gesture.kind === 'zoom' && frameTime - gesture.lastInputTime > WHEEL_GESTURE_IDLE_MS) {
        finish();
        return;
      }

      frameId = requestAnimationFrame(recordFrame);
    }

    function startOrContinue(kind: GestureKind) {
      const now = performance.now();

      if (gesture?.kind === kind) {
        gesture.lastInputTime = now;
        return;
      }

      finish();
      gesture = { kind, startTime: now, lastInputTime: now, frameGaps: [], wasHidden: document.hidden };
      frameId = requestAnimationFrame(recordFrame);
    }

    function finish() {
      const finished = gesture;

      gesture = undefined;

      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
        frameId = undefined;
      }

      if (!finished || finished.wasHidden) {
        return;
      }

      const stutters = getGestureStutters(finished.frameGaps);

      if (isUnusableGesture(stutters)) {
        onSlowGesture({
          kind: finished.kind,
          gestureMs: (finished.lastFrameTime ?? finished.lastInputTime) - finished.startTime,
          stutterCount: stutters.length,
          longestStutterMs: Math.max(...stutters),
        });
      }
    }

    function handleWheel(event: WheelEvent) {
      if (!isOnChartCanvas(event)) {
        return;
      }

      startOrContinue('zoom');
    }

    // A press on the canvas only becomes a pan once the pointer moves with the button held.
    function handlePointerDown(event: PointerEvent) {
      isPointerPressedOnCanvas = isOnChartCanvas(event);
    }

    function handlePointerMove(event: PointerEvent) {
      if (isPointerPressedOnCanvas && event.buttons !== 0) {
        startOrContinue('pan');
      }
    }

    function handlePointerUp() {
      isPointerPressedOnCanvas = false;

      if (gesture?.kind === 'pan') {
        finish();
      }
    }

    function handleVisibilityChange() {
      if (gesture && document.hidden) {
        gesture.wasHidden = true;
      }
    }

    chartElement.addEventListener('wheel', handleWheel, { passive: true });
    chartElement.addEventListener('pointerdown', handlePointerDown);
    // On the window, so a drag that leaves the chart still moves and ends the pan.
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      chartElement.removeEventListener('wheel', handleWheel);
      chartElement.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [chartElement, onSlowGesture]);
}
